/**
 * Live corpus size, in one place.
 *
 * Counts were being hardcoded into copy — the search animation said "Reading
 * 26,911 technologies" long after the corpus passed 27,700, and plan blurbs
 * carry their own "27,000+". Every one of those goes stale silently the next
 * time a university is loaded, and nobody notices because nothing breaks.
 *
 * This reads the homepage analytics document the API already rebuilds from the
 * live corpus at startup, caches it for the session, and hands back a rounded
 * "27,000+" style figure for marketing copy plus the exact number for anywhere
 * precision matters.
 */

import { apiService } from '../services/apiService';

export interface CorpusStats {
  technologies: number;
  universities: number;
  countries: number;
  researchers: number;
}

/** Sensible values if the call fails; refreshed the moment it succeeds. */
const FALLBACK: CorpusStats = {
  technologies: 27743, universities: 37, countries: 6, researchers: 38484,
};

let cached: CorpusStats | null = null;
let inFlight: Promise<CorpusStats> | null = null;
const listeners = new Set<(s: CorpusStats) => void>();

export const getCorpusStatsSync = (): CorpusStats => cached || FALLBACK;

export function loadCorpusStats(): Promise<CorpusStats> {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  const base = (apiService as any).getApiBaseUrl?.() || '';
  inFlight = fetch(`${base}/api/v1/homepage/analytics`, { cache: 'no-store' })
    .then(r => (r.ok ? r.json() : null))
    .then(payload => {
      const s = payload?.stats;
      cached = s
        ? {
            technologies: Number(s.tech_offers) || FALLBACK.technologies,
            universities: Number(s.universities) || FALLBACK.universities,
            countries: Number(s.countries) || FALLBACK.countries,
            researchers: Number(s.researchers) || FALLBACK.researchers,
          }
        : FALLBACK;
      listeners.forEach(l => l(cached!));
      return cached;
    })
    .catch(() => FALLBACK)
    .finally(() => { inFlight = null; });
  return inFlight;
}

export function onCorpusStats(fn: (s: CorpusStats) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** 27,743 -> "27,000+". Rounds DOWN so the claim is never larger than reality. */
export function roundedTechCount(n = getCorpusStatsSync().technologies): string {
  if (n < 1000) return String(n);
  const step = n >= 10000 ? 1000 : 500;
  return `${(Math.floor(n / step) * step).toLocaleString()}+`;
}

/**
 * Replace count tokens in admin-authored copy, so plan descriptions and
 * feature text can say "{tech_count} tech offers" and stay true.
 * Unknown tokens are left alone rather than blanked.
 */
export function fillCounts(text: string, stats: CorpusStats = getCorpusStatsSync()): string {
  if (!text) return text;
  return text
    .replace(/\{tech_count\}/g, roundedTechCount(stats.technologies))
    .replace(/\{tech_count_exact\}/g, stats.technologies.toLocaleString())
    .replace(/\{university_count\}/g, String(stats.universities))
    .replace(/\{country_count\}/g, String(stats.countries))
    .replace(/\{researcher_count\}/g, stats.researchers.toLocaleString())
    // Legacy hardcoded figures that predate the tokens, so existing copy
    // stops being wrong without anyone having to rewrite it first.
    .replace(/\b26,911\b/g, roundedTechCount(stats.technologies))
    .replace(/\b27,000\+/g, roundedTechCount(stats.technologies));
}
